/**
 * V3.4 (2026-06-03) — shared-promise dedup for the defensive /api/me re-fetch.
 *
 * 多个组件 (TopBarClient + WorkspaceMobileHeader + 桌面/移动 ProfileMenu)
 * 都在 mount 时 fetch /api/me 做防御性二次校验 (next-on-pages 1.13 layout
 * caching quirk → 服务端 cookies() 偶发 miss)。三个 mount 同时挂载就是
 * 三个 round-trip。
 *
 * 这里把第一次 fetch 缓在一个 module-scope Promise 上，2 秒窗口内的
 * 后续调用复用同一个 in-flight Promise。窗口足够覆盖首屏全部组件挂载，
 * 又短到不会让一次会话信息变更被永久 cache。失败也不写缓存，下个调用
 * 还能正常重试。
 *
 * 跨 navigation 不复用：每次组件 mount 都会到达 fetchMeClient()，但
 * 由于 window 短，已经超过窗口的会重新发请求 —— 这是 desired，因为
 * 上一次 nav 之后用户角色可能变 (PATCH /me 后路由刷新)。
 */
export type MeClientResponse = {
  user: {
    email: string;
    role: string;
    display_name: string | null;
    username: string | null;
  };
  application?: {
    status: string;
    preferred_branch: string | null;
  } | null;
  // CRO-63 P0 #4 (2026-06-04) — release flags for 撰写 + 发现. Same
  // shape as orchestrator's GET /me response. Optional so older
  // orchestrator deploys keep the field undefined; callers treat
  // missing as "show everything" (= true).
  flags?: {
    writing: boolean;
    discover: boolean;
  };
};

let inflight: Promise<MeClientResponse | null> | null = null;
let inflightAt = 0;

const WINDOW_MS = 2000;

export function fetchMeClient(): Promise<MeClientResponse | null> {
  const now = Date.now();
  if (inflight && now - inflightAt < WINDOW_MS) {
    return inflight;
  }
  inflightAt = now;
  inflight = (async () => {
    try {
      const res = await fetch('/api/me', {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!res.ok) return null;
      const body = (await res.json()) as MeClientResponse;
      if (!body?.user?.role) return null;
      return body;
    } catch {
      return null;
    }
  })();
  // Failures wipe the cache so the next caller retries.
  inflight.then((v) => {
    if (v === null && inflight) {
      inflight = null;
      inflightAt = 0;
    }
  });
  return inflight;
}

/**
 * Test/debug helper — force-clear the cache. Not exported to UI code.
 */
export function __resetMeClientCache(): void {
  inflight = null;
  inflightAt = 0;
}
